// https://docs.appsignal.com/front-end/configuration
// 上の注釈の通り、client側のerror検知のためのAppsignal. ErrorBoundaryのpropsとして渡して使うのが基本。
// server側でもimportしてsendErrorすると使える。

// IncomingMessageはNextApiRequestのextends元。
// context.reqで呼ばれるものがNextApiRequest型でなかったので、継承元のこちらで型付けする。
import type { IncomingMessage } from "http";
import Appsignal from "@appsignal/javascript";

export const appsignal = new Appsignal({
  key: process.env.NEXT_PUBLIC_APPSIGNAL_FRONTEND_KEY,
  revision: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA || "default_revision",
  namespace: `nitoelFrontend-${process.env.NEXT_PUBLIC_KIND || "local"}`,
});

// 誰の操作か、を取得するための関数
export const fetchMe = async (accessToken: string | undefined) => {
  if (!accessToken) return;

  const baseUrl = `${process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL}/private_api`;
  return await fetch(baseUrl + "/me", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then((response) => response.json())
    .catch(() => null);
};

export const xReferer = (req: IncomingMessage): string => {
  return (req.headers["x-nitoel-referer"] as string) || (req.headers["referer"] as string) || "";
};

type MeType = {
  id: string;
  name: string;
  email: string;
  tenantId: string;
};

export const appsignalTags = (me: MeType | undefined | null, referer: string): { [key: string]: string } => {
  if (me) {
    const { id, tenantId } = me;
    // NOTE: nameだけが何故かどうやっても渡すことができず(入れると通知が飛ばなくなる。keyをhogeとかに変えてもだめだった)一旦コメントアウトしておく。
    // let { email, name } = me;
    // name = name.split(" ")[0] + " XXX";
    // - https://docs.appsignal.com/guides/custom-data/tagging-request.html
    // あまり個人情報を送り込んでほしくないらしく、まぁ確かに全部渡す必要もないと思うので何となく分かる程度にマスクする。
    let { email } = me;
    email = "XXX@" + email.split("@")[1];
    return { adminUserId: id, tenantId, email, referer };
  } else {
    return { referer };
  }
};

// Front Door WAF ブロック判定
// 判別軸は content-type ＋ 本文マーカ。x-azure-ref / x-cache は正常応答にも付くため使わない。
// NOTE: 「WAFブロック＝403 かつ 非JSON かつ 本文マーカ」は infra/waf.tf でカスタムブロック応答未設定が前提。
export const detectFrontDoorWafBlock = (status: number, headers: Headers, body: string): boolean => {
  if (status !== 403) return false;
  const contentType = (headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) return false; // バックエンドの認可403(JSON)は除外
  return body.includes("The request is blocked"); // AFD ブロックページの確定マーカ
};

// undici の `TypeError: fetch failed` などは、失敗の実体を err.cause にネストして持つ
// （例: cause = Error: connect ECONNRESET 10.x.x.x:443 / code: "ECONNRESET"）。
// @appsignal/javascript は cause チェーンを自動展開しないため、外側の汎用メッセージ
// （fetch failed）だけが AppSignal に届き、原因種別（接続リセット / タイムアウト / DNS 失敗）が
// 判別できない。ここで cause チェーンを tags に展開し、種別で追える・グルーピングできるようにする。
const MAX_CAUSE_DEPTH = 5;

export const causeTags = (err: unknown): { [key: string]: string } => {
  const chain: string[] = [];
  let deepestCode = "";
  const seen = new Set<unknown>();
  let current: unknown = (err as { cause?: unknown } | null | undefined)?.cause;

  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current != null && !seen.has(current); depth++) {
    seen.add(current);
    const c = current as { name?: string; message?: string; code?: string | number; cause?: unknown };
    const name = c.name || (current instanceof Error ? "Error" : typeof current);
    const message = typeof c.message === "string" ? c.message : String(current);
    const code = c.code != null ? String(c.code) : "";
    if (code) deepestCode = code; // 最下層の code（例: ECONNRESET）が原因種別を最もよく表す
    chain.push(code ? `${name}: ${message} (${code})` : `${name}: ${message}`);
    current = c.cause;
  }

  if (chain.length === 0) return {};
  const tags: { [key: string]: string } = { errorCauseChain: chain.join(" -> ").slice(0, 1000) };
  if (deepestCode) tags.errorCauseCode = deepestCode;
  return tags;
};

export const reportToAppsignal = (err: Error, me: MeType | undefined | null, referer: string) => {
  appsignal.sendError(err, (span) => {
    span.setTags({ ...appsignalTags(me, referer), ...causeTags(err) });
  });
};
